<template>
    <div>
        <button
            class="button button--blank"
            type="button"
            :class="triggerClass"
            @click="toggleModal"
        >
            <slot name="trigger">
                <IconNoteedit class="corrections-add-icon" />
            </slot>
        </button>
        <portal to="modals">
            <Modal
                v-if="isOpen"
                name="corrections-add"
                :modal-class="modalClass"
                :title="
                    isProofreadingReviewer
                        ? $t('textEntityControl.leaveComment')
                        : $t('corrections.add.title')
                "
                @closed="isOpen = false"
            >
                <template #content>
                    <Spinner v-if="loading" />
                    <div v-if="bookId" class="corrections-add">
                        <div class="corrections-add__text">
                            {{ part.raw_text }}
                        </div>
                        <form
                            id="correction-add"
                            class="corrections-add__form"
                            @submit.prevent="save"
                        >
                            <FormField
                                name="comment"
                                class="corrections-add__editor-field"
                                :label="$t('corrections.add.comment')"
                                :error="error"
                                required
                            >
                                <Wysiwyg
                                    v-model="comment"
                                    class="corrections-add__comment"
                                    :placeholder="$t('corrections.add.commentPlaceholder')"
                                />
                            </FormField>
                            <FormField
                                name="is_global"
                                class="corrections-add__option-row"
                                :error="error"
                                help="Check this box to apply to all instances of these words"
                                style="margin-bottom: 12px"
                            >
                                <Checkbox
                                    v-model="is_global"
                                    theme="inverted"
                                    label="Apply corrections to all instances of these words throughout the book."
                                />
                            </FormField>
                            <div v-if="parentCorrection" class="form-group">
                                <div v-t="$t('corrections.add.parent')" class="form-group__label" />
                                <div class="corrections-add__parent-comment">
                                    {{ parentCorrection.id }}. {{ parentCorrection.comment }}
                                </div>
                            </div>
                        </form>
                    </div>
                </template>
                <template #bottom>
                    <div class="modal__confirm corrections-add__footer">
                        <button
                            type="button"
                            class="button button--negative modal__confirm-button corrections-add__cancel"
                            @click.prevent="isOpen = false"
                        >
                            {{ $t('corrections.add.cancel') }}
                        </button>
                        <button
                            :class="[
                                'button modal__confirm-button corrections-add__save',
                                { 'is-disabled': !canSave },
                            ]"
                            type="submit"
                            form="correction-add"
                            :disabled="!canSave"
                        >
                            {{ $t('corrections.add.save') }}
                        </button>
                    </div>
                </template>
            </Modal>
        </portal>
    </div>
</template>

<script>
import { Modal } from '@speechki-frontend/shared/ui/modals';
import { Checkbox, FormField } from '@speechki-frontend/shared/ui/forms';
import cutText from '@speechki-frontend/shared/helpers/functions/cutText';
import Wysiwyg from '@speechki-frontend/shared/ui/Wysiwyg';
import { IconNoteedit } from '@speechki-frontend/shared/ui/icons';
import { mapGetters } from 'vuex';
import { addCorrection } from '../../api/corrections';

const convertHtmlToText = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

export default {
    name: 'CorrectionsAdd',
    components: {
        Modal,
        IconNoteedit,
        Checkbox,
        FormField,
        Wysiwyg,
    },
    inject: {
        isEmbed: {
            default: false,
        },
    },
    props: {
        bookId: {
            type: [String, Number],
            default: 0,
        },
        part: {
            required: true,
            type: Object,
        },
        parentCorrection: {
            type: Object,
            default: null,
        },
        triggerClass: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isOpen: false,
            loading: false,
            comment: '',
            is_global: true,
            error: null,
        };
    },
    computed: {
        ...mapGetters(['isReviewer']),
        isProofreadingReviewer() {
            return this.isEmbed && this.isReviewer;
        },
        modalClass() {
            return this.isProofreadingReviewer
                ? 'corrections-add-modal corrections-add-modal--reviewer'
                : 'corrections-add-modal';
        },
        canSave() {
            const text = convertHtmlToText(this.comment);
            return text.trim().length;
        },
    },
    watch: {
        isOpen() {
            if (this.isOpen) {
                this.comment = '';
                this.error = null;
            }
        },
    },
    methods: {
        cutText,
        toggleModal() {
            this.isOpen = !this.isOpen;
        },
        async save() {
            try {
                this.loading = true;
                this.error = null;

                const data = {
                    book: this.bookId,
                    comment: this.comment,
                    book_part_v2: this.part.id,
                    is_global: this.is_global,
                };

                if (this.parentCorrection) {
                    data.parent = this.parentCorrection.id;
                }

                const correction = await addCorrection(data);

                this.$store.dispatch('pushToast', {
                    type: 'success',
                    title: this.$t('corrections.add.success'),
                });

                this.$emit('add', correction);

                this.isOpen = false;
            } catch (e) {
                console.log(e);
                this.error = e;
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style src="./CorrectionsAdd.scss" lang="scss"></style>
